import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import {
  _findSelectOption,
  _capitalized,
  _isFirstRender,
  _getSkipByPage,
  _getCSRFTokenValue,
  _getTranslation,
  _getPlaceHolderKey,
} from "../utils/helper";
import api from "../utils/api";

import { Loader, Pagination } from "../atoms";
import {
  CardNews,
  CardEvent,
  CardPodcast,
  CardPressRelease,
  CardVideo,
} from "../cards";
import { Datepicker, InputSearch, InputSelect, MobileFilters } from "../forms";

const NewsEventsAndMediaOverview = (props) => {
  const language = props?.data["language"] || "en";
  const nodeType = props?.data["node-type"] || "";
  const isNotArchive = props?.data["archive"] === "no";
  const INITIAL_VALUES = {
    language: language,
    nodeType: nodeType,
    searchTerm: "",
    date: "",
    type: "",
    blogType: "",
    region: "",
    theme: "",
    page: 1,
    tag: "",

    // ASC to show upcoming events in order. reset it to DESC when filtering all events
    orderBy: isNotArchive && nodeType === "event_calendar" ? "ASC" : "DESC",
    upcomingEvents:
      isNotArchive && nodeType === "event_calendar" ? "yes" : "no",
  };

  // delete any irrelevant keys based on node type
  if (nodeType === "event_calendar") {
    delete INITIAL_VALUES.theme;
    delete INITIAL_VALUES.type;
    delete INITIAL_VALUES.blogType;
  } else if (
    nodeType === "podcast" ||
    nodeType === "video" ||
    nodeType === "press_releases"
  ) {
    delete INITIAL_VALUES.upcomingEvents;
    delete INITIAL_VALUES.type;
    delete INITIAL_VALUES.blogType;
  } else if (nodeType === "news") {
    delete INITIAL_VALUES.upcomingEvents;
    delete INITIAL_VALUES.blogType;
  } else if (nodeType === "blog") {
    delete INITIAL_VALUES.upcomingEvents;
    delete INITIAL_VALUES.type;
    delete INITIAL_VALUES.blog_type; // Temporary
  }

  // to reorder more than one filter types
  const filterListOrder = {
    news: ["type", "region", "theme"],
    event_calendar: ["region"],
    podcast: ["region", "theme"],
    speech: ["region", "theme"],
    video: ["region", "theme"],
    press_releases: ["region", "theme"],
    // blog: ["blogType", "region", "theme"],
    blog: ["region", "theme"],
  };
  const [values, setValues] = useState(INITIAL_VALUES);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_VALUES);
  const [posts, setPosts] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [areFiltersActive, setAreFiltersActive] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const isFirstLoad = _isFirstRender();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tag_id = urlParams.get("tag_id");
    const tag_name = urlParams.get("tag_name");
    const region = urlParams.get("region");
    const blog_type = urlParams.get("blog_type");

    if ((tag_id && tag_name) || region || blog_type) {
      let newFilters = {
        ...appliedFilters,
      };

      if (tag_id && tag_name) {
        newFilters.tag = {
          label: tag_name,
          value: parseInt(tag_id),
        };
      }

      if (region) {
        newFilters.region = region;
      }

      if (blog_type) {
        newFilters.blogType = blog_type;
      }

      // On prefill, turn it to "archive mode"
      if (nodeType === "event_calendar") {
        newFilters.upcomingEvents = "";
        newFilters.orderBy = "DESC";
      }

      setAppliedFilters(newFilters);
      setAreFiltersActive(true);
    } else {
      fetchPosts();
    }
  }, [window.location.search]);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    if (!isFirstLoad) {
      // sync the values when filters are applied
      setValues(appliedFilters);

      fetchPosts();
    }
  }, [appliedFilters]);

  const fetchFilters = () => {
    setIsLoadingFilters(true);
    api
      .get("/filterlistoptions?lang=" + document.getElementById("currentLangCode").innerHTML)
      .then((res) => {
        setIsLoadingFilters(false);
        const filtersData = res.data;
        const formattedList = [];
        Object.keys(filtersData).map((key) => {
          formattedList.push({
            label: _capitalized(key),
            key: key,
            options: filtersData[key],
          });
        });
        setFilterOptions(formattedList);
      })
      .catch(() => {
        setIsLoadingFilters(false);
      });
  };

  const fetchPosts = async () => {
    setIsLoadingPosts(true);
    const POSTS_PER_PAGE = nodeType === "news" || !isNotArchive ? 16 : 8;
    const POSTS_SKIP = _getSkipByPage(appliedFilters.page, POSTS_PER_PAGE);
    const fields = {
      ...appliedFilters,
      tag: appliedFilters.tag?.value ?? appliedFilters.tag,
      skip: POSTS_SKIP,
      take: POSTS_PER_PAGE,
    };

    // remove irrelevant keys
    delete fields.page;

    let apiPath = '/newsmedia/search';

    if (nodeType === 'press_releases') {
      apiPath = '/pressreleases/search';
    }

    const csrfToken = await _getCSRFTokenValue();
    api
      .post(apiPath, fields, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        const TOTAL_POSTS = res.data.totalNodes;
        const TOTAL_PAGES = Math.ceil(TOTAL_POSTS / POSTS_PER_PAGE);
        setIsLoadingPosts(false);
        setPosts(res.data.contentData);
        setTotalPages(TOTAL_PAGES);
      })
      .catch(() => {
        setIsLoadingPosts(false);
      });
  };

  const getFilterInputs = () => {
    // NOTE: call this component as function to avoid Datepicker closing on change (for range mode)
    return isLoadingFilters ? (
      <Loader />
    ) : (
      <React.Fragment>
        <div
          className={`filter-item flex flex-col sm:flex-row ${
            nodeType === "news" ? "sm:flex-wrap lg:flex-nowrap" : ""
          } sm:items-center gap-y-3 gap-x-4`}
        >
          <label
            className={`hidden ${
              nodeType === "news" ? "xl:block" : "md:block"
            } font-semibold`}
          >
            {_getTranslation("filter_by")}
          </label>

          <Datepicker
            mode="range"
            value={values.date}
            placeholder={`${_getTranslation('date')}`}
            class={`flex-1 ${
              nodeType === "news" ? "sm:flex-[1_0_48%]" : ""
            } lg:flex-initial`}
            onChange={(selected) => {
              setValues({
                ...values,
                date: selected,
              });
            }}
          />
          {filterListOrder.hasOwnProperty(nodeType) &&
            filterListOrder[nodeType].map((key) => {
              const foundFilterOptions = filterOptions.find(
                (opt) => opt.key === key
              );

              return (
                foundFilterOptions && (
                  <InputSelect
                    class={`flex-1 ${
                      nodeType === "news" ? "sm:flex-[1_0_48%] lg:flex-1" : ""
                    } lg:w-[160px]`}
                    name={foundFilterOptions.key}
                    // placeholder={`All ${foundFilterOptions.key === 'blogType' ? 'type' : foundFilterOptions.key}s`}
                    placeholder={`${_getTranslation(_getPlaceHolderKey(foundFilterOptions.key))}`}
                    options={foundFilterOptions.options}
                    isClearable
                    isSearchable
                    value={_findSelectOption(
                      foundFilterOptions.options,
                      values[foundFilterOptions.key]
                    )}
                    onChange={(name, selectedValues) => {
                      setValues({
                        ...values,
                        [foundFilterOptions.key]: selectedValues,
                      });
                    }}
                  />
                )
              );
            })}
        </div>

        <div className="grid grid-cols-2 sm:flex items-center gap-y-2 gap-x-4 lg:gap-x-2 sm:mt-1 lg:mt-0">
          <button className="btn btn-s--pill" onClick={() => applyFilters()}>
            {_getTranslation("apply_filters")}
          </button>
          {!_.isEqual(values, INITIAL_VALUES) && (
            <button
              className="btn btn-s--pill btn-c--hollow---black"
              onClick={() => resetFilters()}
            >
              {_getTranslation("reset_filters")}
            </button>
          )}
        </div>
      </React.Fragment>
    );
  };

  const removeTag = (tag) => {
    const filterKey = tag?.filterKey;

    if (filterKey) {
      const currentValues = values[filterKey];
      let updatedValues = { ...values };

      if (_.isArray(currentValues)) {
        const newValues = currentValues.filter((item) => item !== tag.value);
        updatedValues[filterKey] = newValues;
      } else if (_.isString(currentValues) || _.isObject(currentValues)) {
        let newValue = "";

        // uncomment this if newValue's default value is not empty string
        // if (filterKey === "search_term") {
        //   newValue = "";
        // }

        updatedValues[filterKey] = newValue;
      }

      // setValues(updatedValues);
      setAppliedFilters(updatedValues);
    }
  };

  const FilterTags = useCallback(() => {
    const selectedTags = [];

    const filterKey = "tag";
    const filterCategory = appliedFilters[filterKey];

    if (!_.isEmpty(filterCategory)) {
      if (_.isObject(filterCategory)) {
        selectedTags.push({
          label: filterCategory.label,
          value: filterCategory.value,
          filterKey,
        });
      }
    }

    return !_.isEmpty(selectedTags) ? (
      <div className="filter-tags-wrapper flex flex-wrap justify-center sm:justify-start -mt-3 lg:-mt-[4.75rem] xl:-mt-3 mb-8">
        {selectedTags.map((tag) => (
          <button
            key={tag}
            className="filter-tag"
            onClick={() => {
              removeTag(tag);
            }}
          >
            {tag.label}
          </button>
        ))}
        <button
          className="filter-tag filter-tag-reset"
          onClick={() => resetFilters()}
        >
          {_getTranslation("clear_all")}
        </button>
      </div>
    ) : null;
  }, [appliedFilters]);

  const applyFilters = (pageNo = 1, isFiltersActive = true) => {
    if (isFiltersActive) {
      setAreFiltersActive(true);

      const newFilters = {
        ...values,
        page: pageNo,
        orderBy: "DESC",
        upcomingEvents: "",
      };

      if (nodeType !== "event_calendar") {
        delete newFilters.upcomingEvents;
      }

      setAppliedFilters(newFilters);
    } else {
      setAppliedFilters({ ...values, page: pageNo });
    }
  };

  const resetFilters = () => {
    setValues(INITIAL_VALUES);
    setAppliedFilters(INITIAL_VALUES);
    setAreFiltersActive(false);
  };

  const SectionHeading = () => {
    switch (nodeType) {
      case "event_calendar":
        if (areFiltersActive) return _getTranslation("all_events");
        if (!areFiltersActive) {
          if (isNotArchive) return _getTranslation("upcoming_events");

          return _getTranslation("past_events");
        }

        return null;

      case "podcast":
        return _getTranslation("all_podcasts");

      case "video":
        return _getTranslation("all_videos");

      // case "news":
      //   return "All news";

      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <div className="container mb-16">
        <h2 className="h2 !mb-4">{props?.data["page-title"]}</h2>

        <div
          className={`desktop-filters hidden sm:flex flex-col ${
            nodeType === "news"
              ? "xl:flex-row xl:items-center xl:justify-end"
              : "lg:flex-row lg:items-center lg:justify-end"
          } flex-wrap gap-y-2 gap-x-4 mb-8`}
        >
          <div
            className={`filter-item flex-1 ${
              nodeType === "news"
                ? "xl:flex-initial xl:mr-auto"
                : "lg:flex-initial lg:mr-auto"
            }`}
          >
            <InputSearch
              class="w-full"
              placeholder={_getTranslation("type_in_your_keywords")}
              value={values.searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setValues({ ...values, searchTerm: value });
              }}
            />
          </div>
          {getFilterInputs()}
        </div>

        <MobileFilters class="w-full sm:hidden mb-6">
          <div className="filter-item">
            <InputSearch
              class="w-full"
              placeholder={_getTranslation("type_in_your_keywords")}
              value={values.searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setValues({ ...values, searchTerm: value });
              }}
            />
          </div>
          {getFilterInputs()}
        </MobileFilters>

        <FilterTags />

        <h4 className="h4 !mb-4">
          <SectionHeading />
        </h4>

        {isLoadingPosts && <Loader class="mb-8" />}
        {_.isArray(posts) && posts.length > 0 ? (
          <React.Fragment>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 mb-8">
              {posts.map((post) => {
                if (post.cardType === "news") return <CardNews data={post} />;
                if (post.cardType === "event") return <CardEvent data={post} />;
                if (post.cardType === "podcast")
                  return <CardPodcast data={post} />;
                if (post.cardType === "video") return <CardVideo data={post} />;
                if (post.cardType === "press_releases")
                  return <CardPressRelease data={post} />;
              })}
            </div>

            <div className="flex items-center justify-center gap-x-6 mt-8">
              <Pagination
                page={appliedFilters.page}
                totalPages={totalPages}
                setPage={(newPage) => {
                  if (areFiltersActive) {
                    applyFilters(newPage);
                  } else {
                    applyFilters(newPage, false);
                  }
                }}
              />
              {isLoadingPosts && <Loader />}
            </div>
          </React.Fragment>
        ) : isLoadingPosts ? null : (
          <p>{_getTranslation("there_are_no_posts_available")}.</p>
        )}
      </div>
    </React.Fragment>
  );
};

export default NewsEventsAndMediaOverview;
